import type { BruteForcePlayer } from './types';

/**
 * Permanent GK ranking score — mirrors the formula used by the tier-based
 * snake draft (gk 70%, gameIq 20%, win-rate-driven performance 10%).
 *
 * Only used to order multiple permanent GKs for alternate team assignment.
 * The absolute value is not exposed anywhere — only relative ordering matters.
 */
export function calculatePermanentGKRating(player: BruteForcePlayer): number {
  const gk = player.gk ?? 5;
  const gameIq = player.gameIq ?? 5;
  const winRate = player.overallWinRate ?? 50;

  return gk * 0.7 + gameIq * 0.2 + (winRate / 100) * 1.0;
}

/**
 * Pre-assign permanent GKs to teams using Phase-0 logic:
 *   1. Sort permanent GKs by ranking (highest first)
 *   2. Alternate Blue, Orange, Blue, Orange…
 *
 * Returns the GKs split into two arrays plus the outfield pool (everyone else).
 */
export function assignPermanentGKs(
  allPlayers: BruteForcePlayer[],
  permanentGKIds: string[]
): {
  blueGKs: BruteForcePlayer[];
  orangeGKs: BruteForcePlayer[];
  outfieldPool: BruteForcePlayer[];
} {
  if (permanentGKIds.length === 0) {
    return { blueGKs: [], orangeGKs: [], outfieldPool: allPlayers };
  }

  const gkSet = new Set(permanentGKIds);
  const gks = allPlayers
    .filter((p) => gkSet.has(p.player_id))
    .map((p) => ({ ...p, isPermanentGK: true }))
    .sort((a, b) => calculatePermanentGKRating(b) - calculatePermanentGKRating(a));

  const blueGKs: BruteForcePlayer[] = [];
  const orangeGKs: BruteForcePlayer[] = [];
  gks.forEach((gk, idx) => {
    if (idx % 2 === 0) blueGKs.push(gk);
    else orangeGKs.push(gk);
  });

  const outfieldPool = allPlayers.filter((p) => !gkSet.has(p.player_id));

  return { blueGKs, orangeGKs, outfieldPool };
}
